import { useEffect, useState } from "react";
import { Coins, Calculator, Gift, Syringe, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { fmtCurrency } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";

const COMMISSION_RULES = [
  ["0% – 10%", "8%"], ["11% – 20%", "6.5%"], ["21% – 30%", "5%"], ["31% – 40%", "3%"], ["Above 40%", "Management Approval"],
];
const APPROVAL_RULES = [
  ["0% – 20%", "BDM Approval"], ["21% – 30%", "Manager Approval"], ["31% – 40%", "Director Approval"], ["Above 40%", "CEO Approval Required"],
];
const EXCLUSIONS = ["Samples", "Free Goods", "Credit Notes", "Returns", "Bad Debts", "Cancelled Orders", "Training Stock", "Warranty Replacements"];

export default function Commissions() {
  const [value, setValue] = useState(10000);
  const [discount, setDiscount] = useState(10);
  const [paid, setPaid] = useState(true);
  const [excluded, setExcluded] = useState(false);
  const [result, setResult] = useState(null);
  const [bonus, setBonus] = useState(null);

  const calc = () => {
    api.post("/commission/calculate", { value: Number(value), discount: Number(discount), invoice_paid: paid, excluded })
      .then((r) => setResult(r.data));
  };

  useEffect(() => { calc(); /* eslint-disable-next-line */ }, [value, discount, paid, excluded]);
  useEffect(() => { api.get("/commission/bonus-programmes").then((r) => setBonus(r.data)); }, []);

  return (
    <div>
      <PageHeader title="Commission Engine" subtitle="Discount-based commission, device & injectable bonuses" icon={Coins} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calculator */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-display font-semibold text-navy-900 flex items-center gap-2 mb-4"><Calculator className="h-4 w-4 text-brand" /> Commission Calculator</h3>
          <div className="space-y-4">
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">Order Value (NZD)</label>
              <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} data-testid="calc-value" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">Discount: {discount}%</label>
              <input type="range" min="0" max="60" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-full accent-navy-900" data-testid="calc-discount" /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Invoice fully paid</span>
              <Switch checked={paid} onCheckedChange={setPaid} data-testid="calc-paid" /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Excluded (sample/free/credit)</span>
              <Switch checked={excluded} onCheckedChange={setExcluded} data-testid="calc-excluded" /></div>
          </div>
          {result && (
            <div className="mt-5 rounded-xl bg-navy-900 p-5 text-white" data-testid="calc-result">
              <div className="text-xs text-blue-200 uppercase tracking-wider">Commission Payable</div>
              <div className="font-display text-3xl font-bold mt-1">{fmtCurrency(result.commission_amount)}</div>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <span>Rate: <strong>{result.rate != null ? `${result.rate}%` : "N/A"}</strong></span>
                <span className="flex items-center gap-1">
                  {result.payable ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}
                  {result.payable ? "Payable" : "Not payable"}
                </span>
              </div>
              <div className="text-xs text-blue-200/80 mt-2">{result.reason} · Routing: {result.approval_level}</div>
            </div>
          )}
        </div>

        {/* Rules */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-display font-semibold text-navy-900 mb-3">Discount → Commission</h3>
            <table className="w-full text-sm">
              <tbody>{COMMISSION_RULES.map(([d, c]) => (
                <tr key={d} className="border-b border-slate-50"><td className="py-2 text-slate-600">{d}</td><td className="py-2 text-right font-semibold text-navy-900">{c}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-display font-semibold text-navy-900 mb-3">Discount Approval Routing</h3>
            <table className="w-full text-sm">
              <tbody>{APPROVAL_RULES.map(([d, a]) => (
                <tr key={d} className="border-b border-slate-50"><td className="py-2 text-slate-600">{d}</td><td className="py-2 text-right font-medium text-brand">{a}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>

        {/* Exclusions */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-display font-semibold text-navy-900 mb-3">Commission Exclusions</h3>
          <p className="text-xs text-slate-400 mb-3">Commission is only payable after an invoice is fully paid. No commission on:</p>
          <div className="flex flex-wrap gap-2">
            {EXCLUSIONS.map((e) => (
              <span key={e} className="text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-600 border border-red-200">{e}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Bonus programmes */}
      {bonus && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-display font-semibold text-navy-900 flex items-center gap-2 mb-4"><Gift className="h-4 w-4 text-brand" /> Device Bonus Programme</h3>
            <div className="space-y-4">
              {bonus.devices.map((d) => (
                <div key={d.device} className="border border-slate-100 rounded-lg p-4" data-testid={`device-bonus-${d.device}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-navy-900">{d.device}</span>
                    <span className="text-sm">{d.units} units · <strong className="text-emerald-600">{fmtCurrency(d.bonus)}</strong> earned</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {d.tiers.map((t) => (
                      <div key={t.units} className={`flex-1 text-center rounded-md py-1.5 text-xs border ${d.units >= t.units ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-400"}`}>
                        {t.units}u<br /><span className="font-semibold">{fmtCurrency(t.bonus)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-display font-semibold text-navy-900 flex items-center gap-2 mb-4"><Syringe className="h-4 w-4 text-brand" /> Injectable Bonus Programme</h3>
            <div className="rounded-xl bg-slate-50 p-4 mb-4">
              <div className="flex items-end justify-between mb-2">
                <div><div className="text-xs text-slate-400">Annual Revenue</div><div className="font-display text-2xl font-bold text-navy-900">{fmtCurrency(bonus.injectable.revenue)}</div></div>
                <div className="text-right"><div className="text-xs text-slate-400">of {fmtCurrency(bonus.injectable.target)}</div><div className="font-semibold text-brand">{bonus.injectable.percent}%</div></div>
              </div>
              <Progress value={Math.min(bonus.injectable.percent, 100)} className="h-2" />
              <div className="mt-2 text-sm">Bonus earned: <strong className="text-emerald-600">{fmtCurrency(bonus.injectable.bonus)}</strong></div>
            </div>
            <div className="space-y-2">
              {bonus.injectable.tiers.map((t) => (
                <div key={t.percent} className={`flex items-center justify-between rounded-lg px-3 py-2 border text-sm ${bonus.injectable.percent >= t.percent ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-100"}`}>
                  <span className="text-slate-600">{t.percent}% of target</span>
                  <span className="font-semibold text-navy-900">{fmtCurrency(t.bonus)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
